// --- Generic utility: Clear all localStorage/sessionStorage entries with a given prefix ---
export function clearAllStorageByPrefix(prefix: string) {
  if (!isWindowDefined()) return
  // Clear from localStorage
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i)
    if (key && key.startsWith(prefix)) {
      localStorage.removeItem(key)
    }
  }
  // Clear from sessionStorage
  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const key = sessionStorage.key(i)
    if (key && key.startsWith(prefix)) {
      sessionStorage.removeItem(key)
    }
  }
}

// --- Clear all checkout progress (local and session) for all users/guests ---
export function clearAllCheckoutProgress() {
  clearAllStorageByPrefix('checkout-progress-')
  if (isWindowDefined()) {
    localStorage.removeItem('checkout-guest-id')
    // List of all known checkout-related session keys
    const sessionKeys = [
      'checkoutFlowType',
      'sessionNumberOfPortions',
      'sessionNumberOfMeals',
      'sessionCategoryId',
    ]
    sessionKeys.forEach(clearCheckoutSession)
  }
}
export interface LocalCheckoutProgress {
  userId: string
  flowType: string
  numberOfPortions: string | null
  numberOfMeals: number | null
  categoryId: string | null
  currentStep: number
  maxCompleted: number
  timestamp: string
}

const STORAGE_KEY_PREFIX = 'checkout-progress-'

function isWindowDefined() {
  return typeof window !== 'undefined'
}

export function getCheckoutProgress(userId: string): LocalCheckoutProgress | null {
  if (!isWindowDefined() || !userId) return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + userId)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export function setCheckoutProgress(userId: string, progress: Omit<LocalCheckoutProgress, 'timestamp'>) {
  if (!isWindowDefined() || !userId) return
  const data: LocalCheckoutProgress = {
    ...progress,
    timestamp: new Date().toISOString(),
  }
  localStorage.setItem(STORAGE_KEY_PREFIX + userId, JSON.stringify(data))
}

export function clearCheckoutProgress(userId: string) {
  if (!isWindowDefined() || !userId) return
  localStorage.removeItem(STORAGE_KEY_PREFIX + userId)
}

export function migrateGuestProgress(guestId: string, newUserId: string) {
  if (!isWindowDefined()) return
  const guest = getCheckoutProgress(guestId)
  if (guest) {
    setCheckoutProgress(newUserId, { ...guest, userId: newUserId })
    clearCheckoutProgress(guestId)
  }
}

export function getOrCreateGuestId(): string {
  if (!isWindowDefined()) return ''
  let guestId = localStorage.getItem('checkout-guest-id')
  if (!guestId) {
    guestId = crypto.randomUUID()
    localStorage.setItem('checkout-guest-id', guestId)
  }
  return guestId
}

// --- Session Storage Methods ---
export function setCheckoutSession(key: string, value: string) {
  if (!isWindowDefined()) return
  sessionStorage.setItem(key, value)
}

export function getCheckoutSession(key: string): string | null {
  if (!isWindowDefined()) return null
  return sessionStorage.getItem(key)
}

export function clearCheckoutSession(key: string) {
  if (!isWindowDefined()) return
  sessionStorage.removeItem(key)
}

export function clearAllCheckoutSession(keys: string[]) {
  if (!isWindowDefined()) return
  keys.forEach(key => sessionStorage.removeItem(key))
}

// --- Unified clear method for both storages by key ---
export function clearCheckoutStorageByKey(key: string) {
  if (!isWindowDefined()) return
  localStorage.removeItem(key)
  sessionStorage.removeItem(key)
}
